import React, { useCallback, useEffect, useState } from 'react'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { CircleCloseIcon } from '@/atoms/Icons/CircleCloseIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Image } from '@/atoms/Image'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { CaptionSM, LabelSM, TitleXXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { getWebClient } from '@/services/ApolloClient'
import { useSaleBanner } from '@/services/ContentfulSaleService/hooks'
import { useGuildMember, useUserGuildTickets } from '@/services/GuildUserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { getObjectFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { DISMISSED_TICKETS_NAV_BANNERS } from '@/utils/local-storage/local-storage-keys'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

const currentTheatrical = {
  slug: slugs.animalFarm,
  logoImagePath: 'v1772473093/studio-app/catalog/a4e51ae2-8ff2-4cbb-a121-9c94433be5fc.webp',
  ticketImagePath: 'v1772727531/angel-studios/nav/Animal_Farm_Tickets.webp',
  mobileBackgroundImagePath: 'v1772727336/angel-studios/nav/animal-farm-mobile.webp',
  desktopBackgroundImagePath: 'v1772727335/angel-studios/nav/animal-farm-desktop.webp',
}

export interface TicketsNavBannerProps {
  hide?: boolean
}

export const TicketsNavBanner: React.FC<TicketsNavBannerProps> = ({ hide = false }) => {
  const track = useSafeTrack()
  const { t } = useTranslate('common')
  const router = useSafeRouter()
  const { loading: bannerLoading, saleWithBanner } = useSaleBanner()
  const { isGuildMember, loading } = useGuildMember()
  const client = getWebClient()
  const [shouldRender, setShouldRender] = useState(false)

  const { numberAvailable: guildTicketsCount = 0, loading: loadingGuildTickets } = useUserGuildTickets({
    theatricalSlug: currentTheatrical.slug,
    client,
    skip: !isGuildMember,
  })

  useEffect(() => {
    const dismissedSlugs = getObjectFromLocalStorage<string[]>(DISMISSED_TICKETS_NAV_BANNERS, [])
    const hasDismissedBanner = dismissedSlugs.includes(currentTheatrical.slug)
    const isGuildJoinPage = router.pathname.includes('/guild/join')
    const isGuildPricingPage = router.pathname.includes('/guild/pricing')
    const isGuildCheckoutPage = router.pathname.includes('/guild/checkout')
    const isMarketingOfferPage = router.pathname.includes('/m/offer')
    const isMovieDetailPage = router.pathname.includes('/movies/')
    const isShowDetailPage = router.pathname.includes('/shows/')

    if (
      hide ||
      isGuildJoinPage ||
      isGuildPricingPage ||
      isGuildCheckoutPage ||
      isMarketingOfferPage ||
      isMovieDetailPage ||
      isShowDetailPage
    ) {
      setShouldRender(false)
    } else if (!loading && !bannerLoading && (isGuildMember || !saleWithBanner)) {
      if (!hasDismissedBanner) {
        setShouldRender(true)
      } else {
        setShouldRender(false)
      }
    }
  }, [hide, router.pathname, isGuildMember, loading, bannerLoading, saleWithBanner])

  const handleTicketsNavBannerDismiss = useCallback(
    (buttonOrigin: boolean, e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      const dismissedSlugs = getObjectFromLocalStorage<string[]>(DISMISSED_TICKETS_NAV_BANNERS, [])
      if (!dismissedSlugs.includes(currentTheatrical.slug)) {
        writeToLocalStorage(DISMISSED_TICKETS_NAV_BANNERS, [...dismissedSlugs, currentTheatrical.slug])
      }
      if (!buttonOrigin) {
        track('Tickets Nav Banner Dismissed', { banner: currentTheatrical.slug })
      }
      setShouldRender(false)
    },
    [track],
  )

  const handleTicketsNavButtonClick = useCallback(() => {
    track('Tickets Nav Banner Button Clicked', { banner: currentTheatrical.slug })
    handleTicketsNavBannerDismiss(true)
  }, [handleTicketsNavBannerDismiss, track])

  return (
    <When condition={shouldRender}>
      <a
        className="group-floating-cta fixed inset-x-0 bottom-0 z-40 flex max-w-full cursor-pointer flex-col items-center bg-black md:absolute md:bottom-auto md:top-0 md:z-50"
        onClick={handleTicketsNavButtonClick}
        href={`${paths.tickets.index}/${currentTheatrical.slug}`}
      >
        <div className="responsive relative h-14 w-full max-w-[1728px] p-3 md:h-16 md:p-4">
          <ResponsiveBackgroundImage
            src={getCloudinaryImageUrl({
              path: currentTheatrical.mobileBackgroundImagePath,
              width: 1024,
            })}
            className="absolute inset-0 h-full w-full bg-cover bg-top lg:hidden"
          />
          <ResponsiveBackgroundImage
            src={getCloudinaryImageUrl({
              path: currentTheatrical.desktopBackgroundImagePath,
              width: 1728,
            })}
            className="absolute inset-0 hidden h-full w-full bg-cover bg-no-repeat lg:block"
          />
          <div className="relative mx-auto flex h-full w-full max-w-[1728px] items-center justify-between">
            <div className="flex flex-row md:gap-[27px]">
              <div className="flex flex-col items-start gap-1 lg:flex-row lg:items-center lg:gap-[27px]">
                <Image
                  src={getCloudinaryImageUrl({
                    path: currentTheatrical.logoImagePath,
                    width: 100,
                  })}
                  alt={t('logo', 'Logo')}
                  className="max-h-[24px] max-w-[34px] lg:max-h-[54px] lg:max-w-[77px]"
                  width={77}
                  height={54}
                />
                <CaptionSM className="lg:photon-title-xs !text-white [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)] lg:font-bold lg:[text-shadow:none]">
                  {t('ticketsAvailableNow', 'Tickets Available Now')}
                </CaptionSM>
              </div>
              <Image
                src={getCloudinaryImageUrl({
                  path: currentTheatrical.ticketImagePath,
                  width: 125,
                })}
                alt={t('twoTickets', 'Two Tickets')}
                className="hidden md:block"
                width={118}
                height={48}
              />
            </div>
            <div className="flex flex-row items-center gap-[35px] md:gap-[42px] lg:gap-[145px]">
              <div>
                <When condition={!loadingGuildTickets}>
                  <LinkButton
                    href={`${paths.tickets.index}/${currentTheatrical.slug}`}
                    variant={'black'}
                    className="rounded-lg border-transparent bg-white/20 px-4 py-2 backdrop-blur-lg hover:bg-white/40"
                    onClick={handleTicketsNavButtonClick}
                  >
                    <LabelSM>
                      {guildTicketsCount && guildTicketsCount > 0
                        ? t('useGuildTickets', 'Use Guild Tickets')
                        : t('getShowtimes', 'Get Showtimes')}
                    </LabelSM>
                  </LinkButton>
                </When>
              </div>
              <div
                onClick={(e) => handleTicketsNavBannerDismiss(false, e)}
                className="cursor-pointer rounded-full lg:rounded-none"
              >
                <div className="hidden cursor-pointer lg:flex lg:flex-row lg:items-center lg:gap-2">
                  <TitleXXS weight="semibold" color="core-gray-200">
                    {t('close', 'Close')}
                  </TitleXXS>
                  <CloseIcon color="core-gray-200" size={24} className="cursor-pointer" />
                </div>
                <div className="lg:hidden">
                  <CircleCloseIcon color="white" size={21} className="cursor-pointer" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </a>

      {/* Spacer to push the site nav below the fixed banner on md+ */}
      <div className="hidden h-16 w-full md:block" aria-hidden="true" />
    </When>
  )
}
